/* eslint-disable no-unused-vars */
import React, { useState, forwardRef, useImperativeHandle, useRef } from "react";
import { Text } from "@fluentui/react";
import { SLStatusCode } from "../../schemas/ISLEntryStatusCodes";
import MultiAutocomplete, { MultiAutocompleteRef } from "../../baseComponents/MultiAutocomplete";

interface ISLSearchDocumentStatusIdAutocompleteProps {}

interface IStatusStringToCode {
  [key: string]: SLStatusCode;
}
export interface ISLSearchDocumentStatusIdAutocompleteRef {
  getDocumentStatus: () => number[];
  setInitialDocumentStatus: (initialDocumentTypes: number[]) => void;
  reset: () => void;
}

const statusCodeStringToSupportedCode: IStatusStringToCode = {
  "Doc Required": SLStatusCode.DocRequired,
  "Sign Off Pending": SLStatusCode.SignOffPending,
  "Awaiting Clarification": SLStatusCode.AwaitingClarification,
  "Draft Recalled": SLStatusCode.DraftRecalled,
  "Signed Off": SLStatusCode.SignedOff,
  Completed: SLStatusCode.Completed
};
const convertDocumentStatusStringToCode = (newValues: string[]) => {
  const ids = newValues
    .filter((documentStatus) => documentStatus in statusCodeStringToSupportedCode)
    .map((status) => statusCodeStringToSupportedCode[status]);
  return ids;
};

const convertDocumentStatusCodeToString = (newValues: number[]) => {
  const statusCodeSupportedCodeToString = {
    [SLStatusCode.DocRequired]: "Doc Required",
    [SLStatusCode.SignOffPending]: "Sign Off Pending",
    [SLStatusCode.AwaitingClarification]: "Awaiting Clarification",
    [SLStatusCode.DraftRecalled]: "Draft Recalled",
    [SLStatusCode.SignedOff]: "Signed Off",
    [SLStatusCode.Completed]: "Completed"
  };
  const idStrings = newValues
    .filter((documentStatus) => documentStatus in statusCodeSupportedCodeToString)
    .map((status: keyof typeof statusCodeSupportedCodeToString) => statusCodeSupportedCodeToString[status] as string);
  return idStrings;
};

const SLSearchDocumentStatusIdAutocomplete = forwardRef((props: ISLSearchDocumentStatusIdAutocompleteProps, ref) => {
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const [_documentStatusIds] = useState<string[]>(Object.keys(statusCodeStringToSupportedCode));
  const autocompleteRef = useRef<MultiAutocompleteRef>(null);

  useImperativeHandle(
    ref,
    (): ISLSearchDocumentStatusIdAutocompleteRef => ({
      getDocumentStatus() {
        if (autocompleteRef.current) {
          return convertDocumentStatusStringToCode(autocompleteRef.current.getSelectedItems());
        }
        return [];
      },
      setInitialDocumentStatus(initialDocumentStatuses) {
        if (autocompleteRef.current) {
          autocompleteRef.current.setInitialSelectedItems(convertDocumentStatusCodeToString(initialDocumentStatuses));
        }
      },
      reset() {
        if (autocompleteRef.current) {
          autocompleteRef.current.reset();
        }
      }
    })
  );

  return (
    <React.Fragment>
      <MultiAutocomplete label="Document Status" required={false} items={_documentStatusIds} ref={autocompleteRef} />
      <Text
        nowrap
        variant="xSmall"
        block
        style={{
          marginTop: "4px",
          color: "var(--accent-font-color, #727272)"
        }}
      >
        Multiple document status ids enabled. Separate by semicolons.
      </Text>
    </React.Fragment>
  );
});

SLSearchDocumentStatusIdAutocomplete.displayName = "SLSearchDocumentStatusIdAutocomplete";

export default SLSearchDocumentStatusIdAutocomplete;
